import { useState } from "react"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup"
import { numberActions } from "../lib/actions"
import type { Grain } from "../lib/salesSeries"
import type { QuickViewData } from "../types"
import SalesGiftsChart from "./SalesGiftsChart"
import Section from "./Section"
import StockChart from "./StockChart"
import StockMovementChart from "./StockMovementChart"

type Graph = "stock" | "sales-gifts" | "movement"

// Selected tab gets the soft primary tint used by the page tabs.
const SELECTED = "data-[state=on]:bg-primary/10 data-[state=on]:font-semibold data-[state=on]:text-primary data-[state=on]:shadow-none"

// One graph at a time: Projected stock first, Sales & Gifts for what went out
// in the past, Movement By Channel for the same units out split by channel,
// by month. Day, Week or Month applies to Sales & Gifts only; the stock line
// is always by day. All mark the product's Push review actions. Sales &
// Gifts also lists this month and last month by channel.
export default function GraphsSection({ data }: { data: QuickViewData }) {
  const [graph, setGraph] = useState<Graph>("stock")
  const [grain, setGrain] = useState<Grain>("day")
  const actions = numberActions(data.actions)

  const header = (
    <div className="text-sm">
      <ToggleGroup type="single" value={graph} onValueChange={(v) => v && setGraph(v as Graph)}>
        <ToggleGroupItem value="stock" size="sm" className={SELECTED}>Projected stock</ToggleGroupItem>
        <ToggleGroupItem value="sales-gifts" size="sm" className={SELECTED}>Sales & Gifts</ToggleGroupItem>
        <ToggleGroupItem value="movement" size="sm" className={SELECTED}>Movement By Channel</ToggleGroupItem>
      </ToggleGroup>
    </div>
  )

  // Sits on the Sales & Gifts caption line, under the tabs, so it never
  // crowds them off a narrow panel.
  const grainSwitch = (
    <ToggleGroup type="single" value={grain} onValueChange={(v) => v && setGrain(v as Grain)} className="text-sm">
      <ToggleGroupItem value="day" size="sm" className={SELECTED}>Day</ToggleGroupItem>
      <ToggleGroupItem value="week" size="sm" className={SELECTED}>Week</ToggleGroupItem>
      <ToggleGroupItem value="month" size="sm" className={SELECTED}>Month</ToggleGroupItem>
    </ToggleGroup>
  )

  return (
    <Section header={header}>
      {graph === "stock" && <StockChart data={data} actions={actions} />}
      {graph === "sales-gifts" && (
        <SalesGiftsChart days={data.sales_history.days} grain={grain} today={data.today} actions={actions} controls={grainSwitch} />
      )}
      {graph === "movement" && <StockMovementChart history={data.sales_history} today={data.today} actions={actions} />}
    </Section>
  )
}
